import type { TeamLiveData, RosterPlayer } from '../types';
import { TEAMS_BY_ABBR } from '../constants/teams';

// ESPN unofficial endpoints. No auth needed; rate-limited but fine for our use.
const BASE = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl';
const CORE = 'https://sports.core.api.espn.com/v2/sports/football/leagues/nfl';

interface CachedTeam { data: TeamLiveData; expiresAt: number; }
const CACHE_KEY = 'nfl-team-cache-v2';
const TTL_MS = 1000 * 60 * 60 * 6; // 6 hours

function loadCache(): Record<string, CachedTeam> {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); } catch { return {}; }
}
function saveCache(cache: Record<string, CachedTeam>) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch { /* quota */ }
}

const CURRENT_SEASON = new Date().getFullYear();

// Best-effort: ESPN FPI (Football Power Index) for a team in a given season.
// Returns null silently on any failure — never throws.
async function fetchFpi(espnId: string, season: number): Promise<{
  fpi?: number; fpiRank?: number; projectedWins?: number; projectedLosses?: number; sosRank?: number;
} | null> {
  try {
    // Try regular season first; fall back to preseason (type=1) in off-season
    for (const type of [2, 1]) {
      const url = `${CORE}/seasons/${season}/types/${type}/teams/${espnId}/powerindex`;
      const res = await fetch(url);
      if (!res.ok) continue;
      const json: any = await res.json();
      const stats: any[] = json?.stats ?? [];
      const find = (name: string) => {
        const s = stats.find(x => x?.name === name);
        const v = s?.value;
        return typeof v === 'number' ? v : undefined;
      };
      const findRank = (name: string) => {
        const s = stats.find(x => x?.name === name);
        const v = s?.rank;
        return typeof v === 'number' ? v : undefined;
      };
      const fpi = find('fpi') ?? find('overallEfficiency');
      const projectedWins = find('projectedWins');
      const projectedLosses = find('projectedLosses');
      const fpiRank = findRank('fpi');
      const sosRank = findRank('strengthOfRecord') ?? findRank('strengthOfSchedule');
      if (fpi !== undefined || projectedWins !== undefined) {
        return { fpi, fpiRank, projectedWins, projectedLosses, sosRank };
      }
    }
  } catch (e) {
    console.warn('[espn fpi] failed', espnId, e);
  }
  return null;
}

const KEY_POSITIONS = new Set(['QB', 'RB', 'WR', 'TE', 'K']);

function pickKeyPlayers(roster: RosterPlayer[]): RosterPlayer[] {
  // Top 1 QB, top 1 RB, top 2 WR, top 1 TE, top 1 K — first-in roster order
  const byPos: Record<string, RosterPlayer[]> = {};
  roster.forEach(p => { (byPos[p.position] ||= []).push(p); });
  const out: RosterPlayer[] = [];
  (['QB', 'RB', 'WR', 'WR', 'TE', 'K'] as const).forEach((pos, i, arr) => {
    const seen = arr.slice(0, i).filter(p => p === pos).length;
    const list = byPos[pos] || [];
    if (list[seen]) out.push(list[seen]);
  });
  return out;
}

export async function fetchTeamLiveData(abbr: string, opts: { force?: boolean } = {}): Promise<TeamLiveData | null> {
  const meta = TEAMS_BY_ABBR[abbr];
  if (!meta) return null;
  const cache = loadCache();
  if (!opts.force && cache[abbr] && cache[abbr].expiresAt > Date.now()) {
    return cache[abbr].data;
  }
  try {
    const [teamRes, rosterRes, fpi] = await Promise.all([
      fetch(`${BASE}/teams/${meta.espnId}`),
      fetch(`${BASE}/teams/${meta.espnId}/roster`),
      fetchFpi(meta.espnId!, CURRENT_SEASON),
    ]);
    if (!teamRes.ok || !rosterRes.ok) throw new Error('ESPN fetch failed');
    const teamJson: any = await teamRes.json();
    const rosterJson: any = await rosterRes.json();

    const team = teamJson?.team ?? {};
    const recordItem = team?.record?.items?.[0]?.stats ?? [];
    const wins = Number(recordItem.find((s: any) => s.name === 'wins')?.value ?? 0);
    const losses = Number(recordItem.find((s: any) => s.name === 'losses')?.value ?? 0);
    const ties = Number(recordItem.find((s: any) => s.name === 'ties')?.value ?? 0);
    const standing: string | undefined = team?.standingSummary;

    const next = team?.nextEvent?.[0];
    const competitors = next?.competitions?.[0]?.competitors ?? [];
    const us = competitors.find((c: any) => c?.team?.abbreviation === meta.abbr);
    const them = competitors.find((c: any) => c?.team?.abbreviation !== meta.abbr);
    const nextGame = them?.team?.abbreviation
      ? {
          opponent: them.team.abbreviation,
          date: next?.date ?? '',
          home: us?.homeAway === 'home',
        }
      : undefined;

    const players: RosterPlayer[] = [];
    const groups: any[] = rosterJson?.athletes ?? [];
    groups.forEach(group => {
      const items: any[] = group?.items ?? [];
      items.forEach(p => {
        players.push({
          id: String(p?.id ?? ''),
          name: p?.fullName ?? p?.displayName ?? 'Unknown',
          position: p?.position?.abbreviation ?? group?.position ?? '',
          jersey: p?.jersey,
          headshot: p?.headshot?.href,
        });
      });
    });

    const data: TeamLiveData = {
      abbr,
      record: { wins, losses, ties },
      standing,
      nextGame,
      ...(fpi ?? {}),
      keyPlayers: pickKeyPlayers(players),
      fullRoster: players,
      loadedAt: Date.now(),
    };

    cache[abbr] = { data, expiresAt: Date.now() + TTL_MS };
    saveCache(cache);
    return data;
  } catch (e) {
    console.warn(`[espn] ${abbr} failed:`, e);
    return cache[abbr]?.data ?? null;
  }
}

export async function fetchAllTeamData(): Promise<Record<string, TeamLiveData>> {
  const results = await Promise.all(
    Object.keys(TEAMS_BY_ABBR).map(async abbr => [abbr, await fetchTeamLiveData(abbr)] as const),
  );
  const out: Record<string, TeamLiveData> = {};
  results.forEach(([abbr, data]) => { if (data) out[abbr] = data; });
  return out;
}

// Power ranking strategy:
//   In-season (any team with completed games): rank by win % (ties weighted 0.5).
//   Off-season (zero games played): rank by ESPN FPI.
//   Anything still missing falls to 32.
export function computePowerRankings(data: Record<string, TeamLiveData>): Record<string, number> {
  const teams = Object.values(data);
  const inSeason = teams.some(t => (t.record?.wins ?? 0) + (t.record?.losses ?? 0) + (t.record?.ties ?? 0) > 0);
  const list = teams.map(t => {
    if (inSeason) {
      const w = t.record?.wins ?? 0;
      const l = t.record?.losses ?? 0;
      const ti = t.record?.ties ?? 0;
      const games = w + l + ti;
      const pct = games ? (w + 0.5 * ti) / games : 0;
      return { abbr: t.abbr, score: pct, tiebreak: games };
    }
    return { abbr: t.abbr, score: t.fpi ?? -999, tiebreak: t.projectedWins ?? 0 };
  });
  list.sort((a, b) => b.score - a.score || b.tiebreak - a.tiebreak);
  const rank: Record<string, number> = {};
  list.forEach((t, i) => { rank[t.abbr] = i + 1; });
  return rank;
}
